import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import type { JoinRequest, JoinRequestsResponse, JoinRequestStatus } from "@mubin/contracts";
import { Badge, Empty, ErrorNote, Field, Loading, Modal, PageTitle, Tabs, useSubmit } from "../components/ui";
import { CreateUserModal } from "../components/user-forms";
import { api, errorText, send } from "../lib/api";
import { joinStatusLabel, personGenderLabel } from "../lib/labels";
import { useCan } from "../lib/org";
import { useMe } from "../lib/session";

export function JoinRequestsPage() {
  const { data: me } = useMe();
  const can = useCan();
  const [status, setStatus] = useState<JoinRequestStatus>("pending");
  const [accepting, setAccepting] = useState<JoinRequest | null>(null);
  const [rejecting, setRejecting] = useState<JoinRequest | null>(null);
  const [copied, setCopied] = useState(false);
  const requests = useQuery({
    queryKey: ["join-requests", me?.activeRole, status],
    queryFn: () => api<JoinRequestsResponse>(`/v1/join-requests?status=${status}`),
  });
  const joinUrl = `${window.location.origin}/download`;

  return (
    <>
      <PageTitle
        title="طلبات الانضمام"
        sub="يقدّمها المتقدمون من تطبيق مُبين دون حساب، ولا يحصلون على أي صلاحية قبل القبول. المجمع والحلقة تُحدد عند القبول."
        actions={
          <button
            className="btn btn-secondary"
            onClick={() => void navigator.clipboard?.writeText(joinUrl).then(() => setCopied(true))}
          >
            <Copy size={16} strokeWidth={1.75} /> {copied ? "تم نسخ الرابط" : "نسخ رابط تحميل التطبيق"}
          </button>
        }
      />
      <section className="card">
        <div className="toolbar">
          <Tabs
            label="حالة الطلب"
            value={status}
            onChange={setStatus}
            items={(["pending", "accepted", "rejected"] as const).map((s) => ({
              value: s,
              label: joinStatusLabel[s],
            }))}
          />
        </div>
        {requests.isPending ? (
          <Loading />
        ) : requests.isError ? (
          <Empty title="تعذر التحميل">{errorText(requests.error)}</Empty>
        ) : requests.data.joinRequests.length === 0 ? (
          <Empty title={status === "pending" ? "لا توجد طلبات بانتظار القرار" : "لا توجد طلبات"}>
            {status === "pending" ? `شارك رابط تحميل التطبيق: ${joinUrl}` : undefined}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>المتقدم</th>
                  <th>المجمع</th>
                  <th>الجوال</th>
                  <th>سنة الميلاد</th>
                  <th>تاريخ الطلب</th>
                  <th>{status === "pending" ? "" : "القرار"}</th>
                </tr>
              </thead>
              <tbody>
                {requests.data.joinRequests.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <b>{r.name}</b> <span className="muted small">· {personGenderLabel[r.gender]}</span>
                      {r.note && <div className="muted small">{r.note}</div>}
                    </td>
                    <td>{r.branchName ?? <span className="muted small">يُحدد عند القبول</span>}</td>
                    <td>
                      <span className="ltr">{r.phone}</span>
                      {r.guardianPhone && (
                        <div className="muted small">
                          ولي الأمر: <span className="ltr">{r.guardianPhone}</span>
                        </div>
                      )}
                    </td>
                    <td className="num">{r.birthYear ?? "—"}</td>
                    <td className="num small">{r.createdAt.slice(0, 10)}</td>
                    <td className="actions">
                      {r.status === "pending" ? (
                        can.has("join.manage") && (
                          <div className="row-actions">
                            <button className="btn btn-primary btn-sm" onClick={() => setAccepting(r)}>
                              قبول
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={() => setRejecting(r)}>
                              رفض
                            </button>
                          </div>
                        )
                      ) : (
                        <>
                          <Badge tone={r.status === "accepted" ? "success" : "neutral"}>
                            {joinStatusLabel[r.status]}
                          </Badge>
                          {r.decisionNote && <div className="muted small">{r.decisionNote}</div>}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <CreateUserModal
        open={accepting !== null}
        onClose={() => setAccepting(null)}
        role="student"
        joinRequest={accepting ?? undefined}
      />
      <RejectModal request={rejecting} onClose={() => setRejecting(null)} />
    </>
  );
}

function RejectModal({ request, onClose }: { request: JoinRequest | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { busy, error, run } = useSubmit();
  const [note, setNote] = useState("");
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!request) return;
    void run(async () => {
      await send("POST", `/v1/join-requests/${request.id}/reject`, { note: note.trim() || undefined });
      await qc.invalidateQueries({ queryKey: ["join-requests"] });
      await qc.invalidateQueries({ queryKey: ["overview"] });
      setNote("");
      onClose();
    }, errorText);
  }
  return (
    <Modal title={`رفض طلب ${request?.name ?? ""}`} open={request !== null} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <Field id="r-note" label="ملاحظة داخلية (اختياري)" hint="لا تُرسل للمتقدم">
          <textarea id="r-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} />
        </Field>
        <div className="form-actions">
          <button className="btn btn-danger" disabled={busy}>
            {busy ? "جارٍ الحفظ…" : "رفض الطلب"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
